import { SearchCollectionType } from "@gtmhub/shared/services/searchService";
import {
  IViewHistoryAutomation,
  IViewHistoryDashboard,
  IViewHistoryGoal,
  IViewHistoryInsight,
  IViewHistoryItemsGroupedResponse,
  IViewHistoryKpi,
  IViewHistoryList,
  IViewHistoryMetric,
  IViewHistorySession,
  IViewHistoryTask,
  IViewHistoryTeam,
  IViewHistoryUser,
  IViewHistoryWhiteboard,
  ViewHistoryItemGrouped,
} from "@gtmhub/view-history";
import { StateParams } from "@uirouter/angularjs";
import { GlobalSearchFacetToggleOptionType, GlobalSearchItemReturnState, GlobalSearchRecentItemsFacetToggleOptionType, returnStatesMap } from "..";
import { MISSING_SESSION_ID_STATE_PARAM } from "@gtmhub/goals/controllers/all";
import { ListTypes } from "@gtmhub/lists";
import { GlobalSearchItemEnriched } from "../models/global-search-item";
import { UXCustomizationType } from "@gtmhub/uxcustomization/models";

export type UXCustomizationTypeGlobalSearch = Exclude<UXCustomizationType, "kpigroup">;

export const searchCollectionTypeToUXCustomizationType: Record<Exclude<SearchCollectionType, "roles">, UXCustomizationTypeGlobalSearch> = {
  sessions: "session",
  goals: "goal",
  metrics: "metric",
  tasks: "task",
  lists: "list",
  users: "user",
  teams: "team",
  dashboards: "dashboard",
  insights: "insight",
  kpis: "kpi",
  automations: "automation",
  whiteboards: "whiteboard",
};

export const uxCustomizationTypeToSearchCollectionType: Record<UXCustomizationTypeGlobalSearch, SearchCollectionType> = {
  session: "sessions",
  goal: "goals",
  metric: "metrics",
  task: "tasks",
  list: "lists",
  user: "users",
  team: "teams",
  dashboard: "dashboards",
  insight: "insights",
  kpi: "kpis",
  automation: "automations",
  whiteboard: "whiteboards",
};

export const globalSearchFacetToRecentItemsFacetMap: Record<GlobalSearchFacetToggleOptionType, GlobalSearchRecentItemsFacetToggleOptionType[]> = {
  everywhere: ["okrs", "employeesAndTeams", "otherItems"],
  okrs: ["okrs"],
  sessions: ["sessions"],
  tasks: ["tasks"],
  teams: ["teams"],
  employees: ["employees"],
  lists: ["lists"],
  insights: ["insights"],
  insightboards: ["insightboards"],
  kpis: ["kpis"],
  automations: ["automations"],
  whiteboards: ["whiteboards"],
};

export const recentItemsTargetTypesByRecentItemsFacetMap: Record<GlobalSearchRecentItemsFacetToggleOptionType, UXCustomizationTypeGlobalSearch[]> = {
  okrs: ["goal", "metric"],
  sessions: ["session"],
  tasks: ["task"],
  employees: ["user"],
  teams: ["team"],
  lists: ["list"],
  insights: ["insight"],
  insightboards: ["dashboard"],
  kpis: ["kpi"],
  automations: ["automation"],
  whiteboards: ["whiteboard"],
  employeesAndTeams: ["user", "team"],
  otherItems: ["session", "task", "list", "insight", "dashboard", "kpi", "automation", "whiteboard"],
};

export const viewHistoryItemCollectionByRecentItemsFacetMap: Record<
  GlobalSearchRecentItemsFacetToggleOptionType,
  Exclude<keyof IViewHistoryItemsGroupedResponse, "totalCount" | "kpiGroupsItems">[]
> = {
  okrs: ["metricItems", "goalItems"],
  sessions: ["sessionItems"],
  tasks: ["taskItems"],
  employees: ["userItems"],
  teams: ["teamItems"],
  lists: ["listItems"],
  insights: ["insightItems"],
  insightboards: ["dashboardItems"],
  kpis: ["kpiItems"],
  automations: ["automationItems"],
  whiteboards: ["whiteboardItems"],
  employeesAndTeams: ["userItems", "teamItems"],
  otherItems: ["sessionItems", "taskItems", "listItems", "insightItems", "dashboardItems", "kpiItems", "automationItems", "whiteboardItems"],
};

const toEnrichedGlobalSearchOKR = (
  item: IViewHistoryGoal | IViewHistoryMetric,
  score: number
): GlobalSearchItemEnriched<"goals"> | GlobalSearchItemEnriched<"metrics"> => {
  if (item.type === "goal") {
    return {
      id: item.itemId,
      score,
      collectionName: "goals",
      nameUI: item.goal.name,
      description: item.goal.description,
      assigneeIds: item.goal.ownerIds || [],
      sessionId: item.goal.sessionId,
      icon: "goal",
      isRecent: true,
      lastVisited: item.lastVisited,
    };
  }

  return {
    id: item.itemId,
    score,
    collectionName: "metrics",
    nameUI: item.metric.name,
    description: item.metric.description,
    assigneeIds: item.metric.ownerIds || [],
    sessionId: item.metric.sessionId,
    goalId: item.metric.goalId,
    icon: "metric",
    isRecent: true,
    lastVisited: item.lastVisited,
  };
};

const toEnrichedGlobalSearchSession = (item: IViewHistorySession, score: number): GlobalSearchItemEnriched<"sessions"> => ({
  id: item.itemId,
  score,
  collectionName: "sessions",
  nameUI: item.session.title,
  icon: "session",
  isRecent: true,
  lastVisited: item.lastVisited,
});

const toEnrichedGlobalSearchTask = (item: IViewHistoryTask, score: number): GlobalSearchItemEnriched<"tasks"> => ({
  id: item.itemId,
  score,
  collectionName: "tasks",
  nameUI: item.task.name,
  description: item.task.description,
  assigneeIds: item.task.ownerId ? [item.task.ownerId] : [],
  icon: "task",
  isRecent: true,
  lastVisited: item.lastVisited,
});

const toEnrichedGlobalSearchEmployee = (item: IViewHistoryUser, score: number): GlobalSearchItemEnriched<"users"> => ({
  id: item.itemId,
  score,
  collectionName: "users",
  fullName: item.user.names,
  email: item.user.email,
  picture: item.user.picture,
  teamIds: [...(item.user.teamIds ? item.user.teamIds : []), ...(item.user.managedTeamIds ? item.user.managedTeamIds : [])],
  isActive: item.user.isActive,
  shouldDisplayAvatar: true,
  shouldDisplayTeams: true,
  isRecent: true,
  lastVisited: item.lastVisited,
});

const toEnrichedGlobalSearchTeam = (item: IViewHistoryTeam, score: number): GlobalSearchItemEnriched<"teams"> => ({
  id: item.itemId,
  score,
  collectionName: "teams",
  nameUI: item.team.name,
  description: item.team.description,
  picture: item.team.picture,
  avatar: item.team.avatar,
  color: item.team.color,
  shouldDisplayTeamAvatar: true,
  isRecent: true,
  lastVisited: item.lastVisited,
});

const toEnrichedGlobalSearchEmployeeOrTeam = (
  item: IViewHistoryUser | IViewHistoryTeam,
  score: number
): GlobalSearchItemEnriched<"users"> | GlobalSearchItemEnriched<"teams"> => {
  if (item.type === "user") {
    return toEnrichedGlobalSearchEmployee(item, score);
  }

  return toEnrichedGlobalSearchTeam(item, score);
};

const toEnrichedGlobalSearchList = (item: IViewHistoryList, score: number): GlobalSearchItemEnriched<"lists"> => {
  let icon: "goal" | "metric" | "task" | "employee" | "team";
  switch (item.list.type) {
    case ListTypes.Objective:
      icon = "goal";
      break;
    case ListTypes["Key Result"]:
      icon = "metric";
      break;
    case ListTypes.Task:
      icon = "task";
      break;
    case ListTypes.Employee:
      icon = "employee";
      break;
    case ListTypes.Team:
      icon = "team";
      break;
  }

  return {
    id: item.itemId,
    score,
    collectionName: "lists",
    nameUI: item.list.title,
    assigneeIds: item.list.ownerId ? [item.list.ownerId] : [],
    type: item.list.type,
    icon,
    isRecent: true,
    lastVisited: item.lastVisited,
  };
};

const toEnrichedGlobalSearchInsight = (item: IViewHistoryInsight, score: number): GlobalSearchItemEnriched<"insights"> => ({
  id: item.itemId,
  score,
  collectionName: "insights",
  icon: "insight",
  nameUI: item.insight.title,
  name: item.insight.name,
  description: item.insight.description,
  isRecent: true,
  lastVisited: item.lastVisited,
});

const toEnrichedGlobalSearchInsightboard = (item: IViewHistoryDashboard, score: number): GlobalSearchItemEnriched<"dashboards"> => ({
  id: item.itemId,
  score,
  collectionName: "dashboards",
  icon: "insightboard",
  nameUI: item.dashboard.name,
  isRecent: true,
  lastVisited: item.lastVisited,
});

const toEnrichedGlobalSearchKPI = (item: IViewHistoryKpi, score: number): GlobalSearchItemEnriched<"kpis"> => ({
  id: item.itemId,
  score,
  collectionName: "kpis",
  nameUI: item.kpi.name,
  description: item.kpi.description,
  assigneeIds: item.kpi.ownerIds ? item.kpi.ownerIds : item.kpi.ownerId ? [item.kpi.ownerId] : [],
  icon: "kpi",
  isRecent: true,
  lastVisited: item.lastVisited,
});

const toEnrichedGlobalSearchAutomation = (item: IViewHistoryAutomation, score: number): GlobalSearchItemEnriched<"automations"> => ({
  id: item.itemId,
  score,
  collectionName: "automations",
  nameUI: item.automation.name,
  description: item.automation.description,
  assigneeIds: item.automation.ownerId ? [item.automation.ownerId] : [],
  icon: "automation",
  isRecent: true,
  lastVisited: item.lastVisited,
});

const toEnrichedGlobalSearchWhiteboard = (item: IViewHistoryWhiteboard, score: number): GlobalSearchItemEnriched<"whiteboards"> => ({
  id: item.itemId,
  score,
  collectionName: "whiteboards",
  nameUI: item.whiteboard?.name,
  icon: "whiteboard",
  isRecent: true,
  lastVisited: item.lastVisited,
});

const toEnrichedGlobalSearchOther = (
  item:
    | IViewHistorySession
    | IViewHistoryTask
    | IViewHistoryList
    | IViewHistoryInsight
    | IViewHistoryDashboard
    | IViewHistoryKpi
    | IViewHistoryAutomation
    | IViewHistoryWhiteboard,
  score: number
):
  | GlobalSearchItemEnriched<"sessions">
  | GlobalSearchItemEnriched<"tasks">
  | GlobalSearchItemEnriched<"lists">
  | GlobalSearchItemEnriched<"insights">
  | GlobalSearchItemEnriched<"dashboards">
  | GlobalSearchItemEnriched<"kpis">
  | GlobalSearchItemEnriched<"automations">
  | GlobalSearchItemEnriched<"whiteboards"> => {
  switch (item.type) {
    case "session":
      return toEnrichedGlobalSearchSession(item, score);
    case "task":
      return toEnrichedGlobalSearchTask(item, score);
    case "list":
      return toEnrichedGlobalSearchList(item, score);
    case "insight":
      return toEnrichedGlobalSearchInsight(item, score);
    case "dashboard":
      return toEnrichedGlobalSearchInsightboard(item, score);
    case "kpi":
      return toEnrichedGlobalSearchKPI(item, score);
    case "automation":
      return toEnrichedGlobalSearchAutomation(item, score);
    case "whiteboard":
      return toEnrichedGlobalSearchWhiteboard(item, score);
  }
};

export const recentItemsCollectionFormatterMap: Record<
  GlobalSearchRecentItemsFacetToggleOptionType,
  (items: ViewHistoryItemGrouped[]) => GlobalSearchItemEnriched<SearchCollectionType>[]
> = {
  okrs: (items: (IViewHistoryGoal | IViewHistoryMetric)[]): (GlobalSearchItemEnriched<"goals"> | GlobalSearchItemEnriched<"metrics">)[] => {
    return items.map((item, idx) => toEnrichedGlobalSearchOKR(item, items.length - idx));
  },
  sessions: (items: IViewHistorySession[]): GlobalSearchItemEnriched<"sessions">[] => {
    return items.map((item, idx) => toEnrichedGlobalSearchSession(item, items.length - idx));
  },
  tasks: (items: IViewHistoryTask[]): GlobalSearchItemEnriched<"tasks">[] => {
    return items.map((item, idx) => toEnrichedGlobalSearchTask(item, items.length - idx));
  },
  employees: (items: IViewHistoryUser[]): GlobalSearchItemEnriched<"users">[] => {
    return items.map((item, idx) => toEnrichedGlobalSearchEmployee(item, items.length - idx));
  },
  teams: (items: IViewHistoryTeam[]): GlobalSearchItemEnriched<"teams">[] => {
    return items.map((item, idx) => toEnrichedGlobalSearchTeam(item, items.length - idx));
  },
  employeesAndTeams: (items: (IViewHistoryUser | IViewHistoryTeam)[]): (GlobalSearchItemEnriched<"users"> | GlobalSearchItemEnriched<"teams">)[] => {
    return items.map((item, idx) => toEnrichedGlobalSearchEmployeeOrTeam(item, items.length - idx));
  },
  lists: (items: IViewHistoryList[]): GlobalSearchItemEnriched<"lists">[] => {
    return items.map((item, idx) => toEnrichedGlobalSearchList(item, items.length - idx));
  },
  insights: (items: IViewHistoryInsight[]): GlobalSearchItemEnriched<"insights">[] => {
    return items.map((item, idx) => toEnrichedGlobalSearchInsight(item, items.length - idx));
  },
  insightboards: (items: IViewHistoryDashboard[]): GlobalSearchItemEnriched<"dashboards">[] => {
    return items.map((item, idx) => toEnrichedGlobalSearchInsightboard(item, items.length - idx));
  },
  kpis: (items: IViewHistoryKpi[]): GlobalSearchItemEnriched<"kpis">[] => {
    return items.map((item, idx) => toEnrichedGlobalSearchKPI(item, items.length - idx));
  },
  automations: (items: IViewHistoryAutomation[]): GlobalSearchItemEnriched<"automations">[] => {
    return items.map((item, idx) => toEnrichedGlobalSearchAutomation(item, items.length - idx));
  },
  whiteboards: (items: IViewHistoryWhiteboard[]): GlobalSearchItemEnriched<"whiteboards">[] => {
    return items.map((item, idx) => toEnrichedGlobalSearchWhiteboard(item, items.length - idx));
  },
  otherItems: (
    items: (
      | IViewHistorySession
      | IViewHistoryTask
      | IViewHistoryList
      | IViewHistoryInsight
      | IViewHistoryDashboard
      | IViewHistoryKpi
      | IViewHistoryAutomation
      | IViewHistoryWhiteboard
    )[]
  ): (
    | GlobalSearchItemEnriched<"sessions">
    | GlobalSearchItemEnriched<"tasks">
    | GlobalSearchItemEnriched<"lists">
    | GlobalSearchItemEnriched<"insights">
    | GlobalSearchItemEnriched<"dashboards">
    | GlobalSearchItemEnriched<"kpis">
    | GlobalSearchItemEnriched<"automations">
    | GlobalSearchItemEnriched<"whiteboards">
  )[] => {
    return items.map((item, idx) => toEnrichedGlobalSearchOther(item, items.length - idx));
  },
};

export const viewHistoryItemsToEnrichedGlobalSearchItemCollection = (
  collection: GlobalSearchRecentItemsFacetToggleOptionType,
  items: ViewHistoryItemGrouped[]
): GlobalSearchItemEnriched<SearchCollectionType>[] => {
  return recentItemsCollectionFormatterMap[collection](items);
};

export const recentItemReturnStatesMap: Record<
  Exclude<GlobalSearchRecentItemsFacetToggleOptionType, "insights">,
  (item: GlobalSearchItemEnriched<SearchCollectionType>) => GlobalSearchItemReturnState
> = {
  sessions: (item: GlobalSearchItemEnriched<"sessions">): GlobalSearchItemReturnState => {
    return {
      state: "gtmhub.goals.all",
      stateParams: { planningSessionId: item.id, page: 1 },
    };
  },
  okrs: (item: GlobalSearchItemEnriched<"goals"> | GlobalSearchItemEnriched<"metrics">): GlobalSearchItemReturnState => {
    const sessionId = item.sessionId || MISSING_SESSION_ID_STATE_PARAM;

    if (item.collectionName === "metrics") {
      return {
        state: "gtmhub.goals.all._goalSidebar.goal._metric.metric",
        stateParams: { id: item.goalId, planningSessionId: sessionId, metricId: item.id },
      };
    } else {
      return {
        state: "gtmhub.goals.all._goalSidebar.goal",
        stateParams: { id: item.id, planningSessionId: sessionId },
      };
    }
  },
  tasks: (item: GlobalSearchItemEnriched<"tasks">): GlobalSearchItemReturnState => {
    return {
      state: "gtmhub.home._task.task",
      stateParams: { taskId: item.id },
    };
  },
  lists: (item: GlobalSearchItemEnriched<"lists">): GlobalSearchItemReturnState => {
    return {
      state: "gtmhub.list",
      stateParams: { listId: item.id, isNewList: false },
    };
  },
  automations: (item: GlobalSearchItemEnriched<"automations">): GlobalSearchItemReturnState => {
    return {
      state: "gtmhub.automation.rules",
      stateParams: { automationId: item.id },
    };
  },
  teams: (item: GlobalSearchItemEnriched<"teams">): GlobalSearchItemReturnState => {
    return {
      state: "gtmhub.teams.team",
      stateParams: { teamId: item.id },
    };
  },
  employees: (item: GlobalSearchItemEnriched<"users">): GlobalSearchItemReturnState => {
    return {
      state: "gtmhub.employees.employee",
      stateParams: { employeeId: item.id },
    };
  },
  employeesAndTeams: (item: GlobalSearchItemEnriched<"users"> | GlobalSearchItemEnriched<"teams">): GlobalSearchItemReturnState => {
    if (item.collectionName === "teams") {
      return returnStatesMap.teams(item);
    } else {
      return returnStatesMap.employees(item);
    }
  },
  kpis: (item: GlobalSearchItemEnriched<"kpis">): GlobalSearchItemReturnState => {
    return {
      state: "gtmhub.kpis._singleKpi.form",
      stateParams: { kpiId: item.id },
    };
  },
  insightboards: (item: GlobalSearchItemEnriched<"dashboards">): GlobalSearchItemReturnState => {
    return {
      state: "gtmhub.insightboard",
      stateParams: { dashboardId: item.id },
    };
  },
  whiteboards: (item: GlobalSearchItemEnriched<"whiteboards">): GlobalSearchItemReturnState => {
    return {
      state: "gtmhub.whiteboard",
      stateParams: { whiteboardId: item.id },
    };
  },
  otherItems: (
    item:
      | GlobalSearchItemEnriched<"sessions">
      | GlobalSearchItemEnriched<"tasks">
      | GlobalSearchItemEnriched<"lists">
      | GlobalSearchItemEnriched<"dashboards">
      | GlobalSearchItemEnriched<"kpis">
      | GlobalSearchItemEnriched<"automations">
      | GlobalSearchItemEnriched<"whiteboards">
  ): GlobalSearchItemReturnState => {
    switch (item.collectionName) {
      case "sessions":
        return returnStatesMap.sessions(item);
      case "tasks":
        return returnStatesMap.tasks(item);
      case "lists":
        return returnStatesMap.lists(item);
      case "dashboards":
        return returnStatesMap.insightboards(item);
      case "kpis":
        return returnStatesMap.kpis(item);
      case "automations":
        return returnStatesMap.automations(item);
      case "whiteboards":
        return returnStatesMap.whiteboards(item);
    }
  },
};

export const getRecentItemReturnState = (
  facet: Exclude<GlobalSearchRecentItemsFacetToggleOptionType, "insights">,
  item: GlobalSearchItemEnriched<SearchCollectionType>
): GlobalSearchItemReturnState => {
  return recentItemReturnStatesMap[facet](item);
};

export const getViewHistoryRejectedIdsByCurrentState = (currentState: string, stateParams: StateParams): string[] => {
  const currentPageItemIds: string[] = [];

  switch (currentState) {
    case "gtmhub.goals.all": {
      currentPageItemIds.push(stateParams.planningSessionId);
      break;
    }
    case "gtmhub.insightboard": {
      currentPageItemIds.push(stateParams.dashboardId);
      break;
    }
    case "gtmhub.list": {
      currentPageItemIds.push(stateParams.listId);
      break;
    }
  }

  return currentPageItemIds;
};
